<template>
  <div class="login-page">
    <el-card class="login-card" shadow="always">
      <div class="login-title">设备管理系统测试版</div>
      <div class="login-sub">请使用后台账号登录</div>
      <el-form @submit.native.prevent="onLogin" :model="form">
        <el-form-item>
          <el-input
            v-model="form.username"
            placeholder="用户名"
            prefix-icon="el-icon-user"
            @keyup.enter.native="onLogin"
          />
        </el-form-item>
        <el-form-item>
          <el-input
            v-model="form.password"
            type="password"
            placeholder="密码"
            prefix-icon="el-icon-lock"
            show-password
            @keyup.enter.native="onLogin"
          />
        </el-form-item>
        <el-button
          type="primary"
          class="login-btn"
          :loading="loading"
          @click="onLogin"
          >登 录</el-button
        >
        <div v-if="error" class="login-error">{{ error }}</div>
      </el-form>
    </el-card>
    <beian-footer />
  </div>
</template>

<script>
import api from '../utils/request'
import BeianFooter from '../components/BeianFooter.vue'

// 腾讯验证码 2.0 SDK（按需动态加载，只进登录页）。
// 2.0 的 JS 文件是 TJCaptcha.js（1.0 是 TCaptcha.js），加载后注册全局类 TencentCaptcha。
// 极少数版本注册为 TJCaptcha，故 getCaptchaCtor 两者都兼容。
const CAPTCHA_SDK_URL = 'https://turing.captcha.qcloud.com/TJCaptcha.js'

function getCaptchaCtor() {
  return window.TencentCaptcha || window.TJCaptcha || null
}

export default {
  name: 'Login',
  components: { BeianFooter },
  data() {
    return {
      form: { username: '', password: '' },
      loading: false,
      error: '',
      captchaEnabled: false, // 后端是否启用了滑块（探测 /web/captcha/status/）
      captchaAppId: '',
      captchaSdkLoading: false, // SDK 是否已在加载中（防重复注入 <script>）
    }
  },
  created() {
    this.detectCaptcha()
  },
  methods: {
    // 探测后端滑块开关；探测失败按未启用处理（后端会在登录时兜底拒绝）
    async detectCaptcha() {
      try {
        const res = await api.get('/web/captcha/status/')
        this.captchaEnabled = !!res.data.enabled
        this.captchaAppId = res.data.app_id || ''
      } catch (e) {
        this.captchaEnabled = false
      }
    },
    loadCaptchaSdk() {
      return new Promise((resolve, reject) => {
        if (getCaptchaCtor()) return resolve()
        if (this.captchaSdkLoading) {
          // 已有加载任务，等它结束
          const timer = setInterval(() => {
            if (getCaptchaCtor()) {
              clearInterval(timer)
              resolve()
            }
          }, 100)
          setTimeout(() => {
            clearInterval(timer)
            if (!getCaptchaCtor()) reject(new Error('滑块组件加载超时'))
          }, 8000)
          return
        }
        this.captchaSdkLoading = true
        const s = document.createElement('script')
        s.src = CAPTCHA_SDK_URL
        s.onload = () => {
          this.captchaSdkLoading = false
          if (getCaptchaCtor()) resolve()
          else reject(new Error('滑块组件初始化失败'))
        }
        s.onerror = () => {
          this.captchaSdkLoading = false
          reject(new Error('滑块组件加载失败，请检查网络'))
        }
        document.head.appendChild(s)
      })
    },
    // 弹出滑块，用户通过后 resolve({ticket, randstr})；取消/失败 reject
    showCaptcha() {
      return new Promise((resolve, reject) => {
        this.loadCaptchaSdk()
          .then(() => {
            const Ctor = getCaptchaCtor()
            const captcha = new Ctor(
              this.captchaAppId,
              (res) => {
                if (res.ret === 0 && res.ticket && res.randstr) {
                  resolve({ ticket: res.ticket, randstr: res.randstr })
                } else if (res.ret === 2) {
                  reject(new Error('请先完成滑块验证'))
                } else {
                  reject(new Error('滑块验证未通过，请重试'))
                }
              },
              { type: 'popup' }
            )
            captcha.show()
          })
          .catch(reject)
      })
    },
    async onLogin() {
      this.error = ''
      if (!this.form.username || !this.form.password) {
        this.error = '请输入用户名和密码'
        return
      }
      // 启用滑块时：先弹滑块拿票据，再提交登录
      let captcha = { ticket: '', randstr: '' }
      if (this.captchaEnabled) {
        try {
          captcha = await this.showCaptcha()
        } catch (e) {
          this.error = e.message || '滑块验证未完成'
          return
        }
      }
      this.loading = true
      try {
        const res = await api.post('/web/login/', {
          username: this.form.username,
          password: this.form.password,
          ticket: captcha.ticket,
          randstr: captcha.randstr,
        })
        const { token, username } = res.data
        localStorage.setItem('ems_web_token', token)
        localStorage.setItem('ems_web_username', username)
        this.$message.success('登录成功')
        this.$router.replace(this.$route.query.redirect || '/')
      } catch (e) {
        const data = e.response && e.response.data
        const detail = (data && (data.detail || data.non_field_errors)) || '登录失败'
        this.error = Array.isArray(detail) ? detail[0] : detail
        // 后端启用了滑块但本页未探测到（如刚部署后没刷新）：现场补开滑块
        if (data && data.captcha_required && !this.captchaEnabled) {
          this.captchaEnabled = true
          try {
            const st = await api.get('/web/captcha/status/')
            this.captchaAppId = st.data.app_id || ''
          } catch (ignored) {
            /* 保底：下次登录再试 */
          }
        }
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.login-page {
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(180deg, #e6f0f9 0%, #d7e8f7 100%);
  position: relative;
}
/* 登录页备案页脚贴底（Home 页内为 flex 常驻底栏，此处绝对定位） */
.login-page .beian-footer {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
}
.login-card {
  width: 380px;
  padding: 12px 8px;
}
.login-title {
  text-align: center;
  font-size: 24px;
  font-weight: bold;
  color: #1e4266;
  letter-spacing: 2px;
  line-height: 1.4;
}
.login-sub {
  text-align: center;
  color: #888;
  margin: 8px 0 20px;
  font-size: 14px;
}
.login-btn {
  width: 100%;
  font-size: 16px;
  font-weight: bold;
  background: #409eff;
  border-color: #409eff;
}
.login-error {
  margin-top: 12px;
  color: #f56c6c;
  font-size: 13px;
  text-align: center;
}
</style>
